/**
 * AWS WAF Silent Token — loads jsapi.js on first call, mints a token via
 * AwsWafIntegration.getToken(). Non-alpha or failures resolve to undefined (fail-open).
 */

import { WAF_ENABLED, WAF_INTEGRATION_URL, WAF_API_KEY, WAF_TOKEN_DOMAIN } from 'api/appConfig';

declare global {
  interface Window {
    awsWafCookieDomainList?: string[];
    AwsWafIntegration?: {
      getToken: (opts: { apiKey: string }) => Promise<string>;
    };
  }
}

// Token cache (~4min TTL, refresh a minute before expiry).
let cachedToken: string | undefined;
let tokenExpiry = 0;
const TOKEN_TTL_MS = 240_000;

let sdkLoaded = false;
let sdkLoadPromise: Promise<void> | null = null;
const SDK_LOAD_TIMEOUT_MS = 5000;

// After a token-acquisition failure, skip WAF entirely for this cooldown.
let failedAt = 0;
const WAF_FAILURE_BACKOFF_MS = 60_000;

// Collapse concurrent cold-start callers onto a single in-flight mint.
let tokenPromise: Promise<string | undefined> | null = null;

function loadSdk(): Promise<void> {
  if (sdkLoaded) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  window.awsWafCookieDomainList = [WAF_TOKEN_DOMAIN];

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${WAF_INTEGRATION_URL}/jsapi.js`;
    script.defer = true;

    const timeout = setTimeout(() => {
      sdkLoadPromise = null;
      reject(new Error('WAF jsapi.js load timed out'));
    }, SDK_LOAD_TIMEOUT_MS);
    // Unref the timer so it doesn't keep Node/Jest workers alive when jsdom
    // can't fire script onload/onerror. In browsers, setTimeout returns a
    // number — .unref is undefined, so optional chaining makes this a no-op.
    (timeout as any)?.unref?.();

    script.onload = () => {
      clearTimeout(timeout);
      sdkLoaded = true;
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeout);
      sdkLoadPromise = null;
      reject(new Error('WAF jsapi.js failed to load'));
    };
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

/** Returns a WAF token, or undefined on failure/disabled stages (fail-open). */
export async function getWafToken(): Promise<string | undefined> {
  if (!WAF_ENABLED) return undefined;
  if (cachedToken && Date.now() < tokenExpiry) return cachedToken;
  if (failedAt && Date.now() - failedAt < WAF_FAILURE_BACKOFF_MS) return undefined;
  if (tokenPromise) return tokenPromise;

  tokenPromise = (async () => {
    try {
      await loadSdk();
      if (!window.AwsWafIntegration) return undefined;
      const t = await window.AwsWafIntegration.getToken({ apiKey: WAF_API_KEY });
      cachedToken = t;
      tokenExpiry = Date.now() + TOKEN_TTL_MS;
      failedAt = 0;
      return t;
    } catch {
      failedAt = Date.now();
      return undefined;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}
